import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';

// Styled Components
const Card = styled.div`
  background-color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.card.background : 'white'};
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.card.shadow
      : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? props.theme.colors.card.border : '#e2e8f0'};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.primary : 'inherit'};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const TableHeader = styled.th`
  padding: 12px;
  text-align: left;
  background-color: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc'};
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.text.secondary
      : '#374151'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};
  font-weight: ${(props) => (props.bold ? '600' : 'normal')};
  color: ${(props) => props.color || 'inherit'};
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${(props) =>
    props.theme.name === 'dark' ? props.theme.colors.text.light : '#64748b'};
`;

// Skeleton Loading Components
const SkeletonRow = styled.tr``;

const SkeletonCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};
`;

const SkeletonBar = styled.div`
  height: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  width: ${(props) => props.width || '100%'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonBadge = styled.div`
  height: 20px;
  width: 60px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// Generate skeleton rows based on column configuration
const SkeletonTable = ({ columns, rowCount = 5 }) => {
  const getSkeletonContent = (column) => {
    switch (column.type) {
      case 'badge':
        return <SkeletonBadge />;
      case 'currency':
        return <SkeletonBar width="80px" />;
      case 'date':
        return <SkeletonBar width="90px" />;
      case 'number':
        return <SkeletonBar width="60px" />;
      case 'link':
        return <SkeletonBar width="70px" />;
      case 'action':
        return <SkeletonBar width="40px" />;
      default:
        return (
          <SkeletonBar width={`${Math.floor(Math.random() * 40) + 60}%`} />
        );
    }
  };

  return (
    <Table>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <TableHeader key={column.key || index}>{column.header}</TableHeader>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rowCount }, (_, rowIndex) => (
          <SkeletonRow key={rowIndex}>
            {columns.map((column, colIndex) => (
              <SkeletonCell key={column.key || colIndex}>
                {getSkeletonContent(column)}
              </SkeletonCell>
            ))}
          </SkeletonRow>
        ))}
      </tbody>
    </Table>
  );
};

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// Helper function to format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Eye icon for view action
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

// Cell content renderer based on column type
const renderCellContent = (value, column, rowData, theme) => {
  switch (column.type) {
    case 'currency':
      return formatCurrency(value);

    case 'date':
      return formatDate(value);

    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : value;

    case 'badge':
      const variant = column.getVariant
        ? column.getVariant(value, rowData)
        : 'neutral';
      return (
        <Badge variant={variant} uppercase>
          {value}
        </Badge>
      );

    case 'link':
      const linkTo = column.getLinkTo ? column.getLinkTo(rowData) : '#';
      return (
        <StyledLink theme={theme} to={linkTo}>
          {value}
        </StyledLink>
      );

    case 'action':
      const actionLinkTo = column.getLinkTo ? column.getLinkTo(rowData) : '#';
      return (
        <Link to={actionLinkTo} style={{ textDecoration: 'none' }}>
          <ButtonV2 variant="icon" icon={<EyeIcon />} title="View" />
        </Link>
      );

    default:
      return value;
  }
};

const TableSection = ({
  title,
  action,
  columns,
  data = [],
  loading = false,
  emptyMessage = 'No data available.',
  skeletonRows = 5,
  testDelay = 0, // Add delay in milliseconds for testing skeleton
}) => {
  const theme = useTheme();
  const [isShowingSkeleton, setIsShowingSkeleton] = useState(testDelay > 0);

  useEffect(() => {
    if (testDelay > 0) {
      // Force skeleton to show for testDelay duration regardless of loading state
      setIsShowingSkeleton(true);
      const timer = setTimeout(() => {
        setIsShowingSkeleton(false);
      }, testDelay);

      return () => clearTimeout(timer);
    }
  }, [testDelay]);

  // Show skeleton if we're in test mode OR if actually loading
  const showSkeleton = isShowingSkeleton || loading;

  return (
    <Card theme={theme}>
      <CardHeader>
        <CardTitle theme={theme}>{title}</CardTitle>
        {action && action}
      </CardHeader>

      <TableContainer>
        {showSkeleton ? (
          <SkeletonTable columns={columns} rowCount={skeletonRows} />
        ) : data.length === 0 ? (
          <LoadingMessage theme={theme}>{emptyMessage}</LoadingMessage>
        ) : (
          <Table>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <TableHeader key={column.key || index}>
                    {column.header}
                  </TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={row.id || rowIndex}>
                  {columns.map((column, colIndex) => {
                    const value = row[column.key];
                    const cellContent = renderCellContent(
                      value,
                      column,
                      row,
                      theme
                    );

                    return (
                      <TableCell
                        key={column.key || colIndex}
                        bold={column.bold}
                        color={
                          column.color === 'primary'
                            ? theme.colors.primary
                            : column.color
                        }
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>
    </Card>
  );
};

export default TableSection;
